import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { vehicle as rules } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FormControl,
  TextField,
  IconButton,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { ActionCategoryLabels } from 'further-ui/labels';
import { Delete } from '@mui/icons-material';
import Table from 'further-ui/components/Table';
import FullHeightView from 'components/new/FullHeightView';
import LazySection from 'components/new/LazySection';
import TransactionTypeSelector from 'components/new/TransactionTypeSelector';
import { z } from 'zod';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import {
  ActionTransactionSubTypes,
  ActionCategory,
  ActionRef,
  ActionTypes as ActionTypesEnum,
} from 'further-types/lp/action';
import { AccountMapping } from 'further-types/lp/account';
import { ApiResponses } from 'lib/trpcClient';
import { groupBy, sortBy } from 'lodash';
import TooltipTriggerText from 'components/new/TooltipTriggerText';
import { getActionLabel, getActionTooltip } from './processLabels';
import {
  fxOnReconciliationPartialSortIndex,
  isFxOnReconciliationPartialRow,
  isHiddenAccountMappingRow,
} from './accountMappingRowVisibility';
import { Column, Row } from 'further-ui/components/Layout';
import PageSection from 'components/new/PageSection';

type ActionTypesFromApi = ApiResponses['action']['listTypes'];

const ContentWrapper = styled(Column)(({ theme }) => ({
  paddingBottom: theme.spacing(theme.spacingSizes.lg),
}));

type FormValues = z.infer<typeof rules.UpdateAccountMappings>;

type AccountMappingWithOriginalIndex = AccountMapping & {
  id: string;
  originalIndex: number;
  isCustom?: boolean;
};

type Props = {
  vehicleId: string;
  actionTypes: ActionTypesFromApi;
  defaultAccountMappings: Array<AccountMapping>;
};

const categoryOrder: Array<ActionCategory> = [
  ActionCategory.Income,
  ActionCategory.Expense,
  ActionCategory.Investment,
  ActionCategory.Realisation,
  ActionCategory.ManagementFees,
  ActionCategory.CapitalActions,
  ActionCategory.FX,
];

const AccountMappingGrid: React.FC<Props> = ({
  vehicleId,
  defaultAccountMappings,
}) => {
  const utils = api.useUtils();

  const { control, handleSubmit, getValues, reset } = useForm<FormValues>({
    resolver: zodResolver(rules.UpdateAccountMappings),
    defaultValues: {
      accountMappings: defaultAccountMappings,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'accountMappings',
  });

  // Reset the form when defaultAccountMappings changes (after refetch)
  useEffect(() => {
    reset({
      accountMappings: defaultAccountMappings,
    });
  }, [defaultAccountMappings, reset]);

  const editMappings = api.vehicle.updateAccountMappings.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Account mappings updated.',
      onSuccess: async () => {
        // Force refetch the vehicle query to get updated account mappings with new custom action type IDs
        await utils.vehicle.getById.refetch({ vehicleId });
        await utils.action.listTypes.refetch({ vehicleId });
      },
      onError: async () => {
        // Refetch on error to ensure data is in sync
        await utils.vehicle.getById.refetch({ vehicleId });
        await utils.action.listTypes.refetch({ vehicleId });
      },
    }),
  );

  const onSubmit = (data: FormValues) => {
    editMappings.mutate({
      vehicleId,
      accountMappings: data.accountMappings,
    });
  };

  const getActionRefForCategory = (category: ActionCategory): ActionRef => {
    switch (category) {
      case ActionCategory.Income:
      case ActionCategory.Expense:
        return ActionRef.IncomeOrExpense;
      case ActionCategory.Investment:
        return ActionRef.Investment;
      case ActionCategory.Realisation:
        return ActionRef.Realisation;
      case ActionCategory.ManagementFees:
        return ActionRef.Fees;
      case ActionCategory.CapitalActions:
        return ActionRef.CapitalAction;
      case ActionCategory.FX:
        return ActionRef.BankAccountTransfer;
      default:
        return ActionRef.IncomeOrExpense;
    }
  };

  const isCustomMapping = (mapping: AccountMapping) => {
    return mapping.actionType === ActionTypesEnum.CustomAction;
  };

  const addCustomMapping = (category: ActionCategory) => {
    const newMapping = {
      actionRef: getActionRefForCategory(category),
      actionType: ActionTypesEnum.CustomAction,
      actionCategory: category,
      customActionType: '',
      customActionTypeId: null,
      transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
      debitTransactionTypeId: '',
      creditTransactionTypeId: '',
    };
    append(newMapping);
  };

  const removeCustomMapping = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  // Group mappings by category while preserving original indices
  const { categorySections, fxOnReconciliationRows } = useMemo(() => {
    const mappingsWithIndices = fields.map((field, index) => ({
      ...field,
      customActionTypeId: field.customActionTypeId,
      originalIndex: index,
      isCustom: isCustomMapping(field),
    }));

    const visibleRows = mappingsWithIndices.filter(
      (m) => !isHiddenAccountMappingRow(m),
    );

    const fxOnReconciliation = sortBy(
      visibleRows.filter(isFxOnReconciliationPartialRow),
      (row) => fxOnReconciliationPartialSortIndex(row),
      (row) => row.actionType,
    );

    const rowsForCategoryTables = visibleRows.filter(
      (m) => !isFxOnReconciliationPartialRow(m),
    );

    const grouped = groupBy(rowsForCategoryTables, (m) => m.actionCategory);

    const sections = categoryOrder
      .filter((category) => grouped[category]?.length > 0)
      .map((category) => {
        const categoryMappings = grouped[category];
        const regularMappings = categoryMappings.filter((m) => !m.isCustom);
        const customMappings = categoryMappings.filter((m) => m.isCustom);

        const sortedRegular = sortBy(
          regularMappings,
          'actionType',
          'transactionSubType',
        );

        return {
          category,
          mappings: [...sortedRegular, ...customMappings],
        };
      });

    return {
      categorySections: sections,
      fxOnReconciliationRows: fxOnReconciliation,
    };
  }, [fields]);

  const createColumns = () => [
    {
      key: 'actionRef',
      label: 'Action',
      width: 200,
      minWidth: 200,
      render: (row: AccountMappingWithOriginalIndex) => (
        <Controller
          name={`accountMappings.${row.originalIndex}.actionRef`}
          control={control}
          defaultValue={row.actionRef}
          render={() => (
            <FormControl fullWidth>
              {row.isCustom ? (
                <Controller
                  name={`accountMappings.${row.originalIndex}.customActionType`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      placeholder="Enter custom action name"
                      size="small"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              ) : (
                <Tooltip
                  title={getActionTooltip(row) || ''}
                  disableHoverListener={!getActionTooltip(row)}
                  arrow
                >
                  <span>
                    {getActionTooltip(row) ? (
                      <TooltipTriggerText
                        variant="body2"
                        color="text.secondary"
                        content={getActionLabel(row)}
                        icon
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                      >
                        {getActionLabel(row)}
                      </Typography>
                    )}
                  </span>
                </Tooltip>
              )}
            </FormControl>
          )}
        />
      ),
    },
    {
      key: 'debitTransactionTypeId',
      label: 'Debit',
      width: 200,
      render: (row: AccountMappingWithOriginalIndex) => (
        <Controller
          name={`accountMappings.${row.originalIndex}.debitTransactionTypeId`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TransactionTypeSelector
              vehicleId={vehicleId}
              disabled={
                getValues(
                  `accountMappings.${row.originalIndex}.debitTransactionTypeId`,
                ) === null
              }
              name={field.name}
              error={error}
              value={field.value ?? undefined}
              onChange={(value) => field.onChange(value)}
            />
          )}
        />
      ),
    },
    {
      key: 'creditTransactionTypeId',
      label: 'Credit',
      width: 200,
      render: (row: AccountMappingWithOriginalIndex) => (
        <Controller
          name={`accountMappings.${row.originalIndex}.creditTransactionTypeId`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TransactionTypeSelector
              vehicleId={vehicleId}
              disabled={
                getValues(
                  `accountMappings.${row.originalIndex}.creditTransactionTypeId`,
                ) === null
              }
              name={field.name}
              error={error}
              value={field.value ?? undefined}
              onChange={(value) => field.onChange(value)}
            />
          )}
        />
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 40,
      render: (row: AccountMappingWithOriginalIndex) =>
        row.isCustom ? (
          <Tooltip
            title={
              row.isInUse
                ? 'Cannot delete. This mapping is currently in use'
                : ''
            }
          >
            <span>
              <IconButton
                onClick={() => removeCustomMapping(row.originalIndex)}
                edge="start"
                size="small"
                disabled={row.isInUse}
                aria-label={`Delete ${row.customActionType || 'mapping'}`}
              >
                <Delete
                  color={row.isInUse ? 'disabled' : 'action'}
                  fontSize="medium"
                />
              </IconButton>
            </span>
          </Tooltip>
        ) : null,
    },
  ];

  const columns = useMemo(
    () => createColumns(),
    [control, getValues, removeCustomMapping],
  );

  const showFxOnReconciliation = fxOnReconciliationRows.length > 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FullHeightView
        actionBar={
          <Row fullWidth justify="flex-end">
            <Button
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              loading={editMappings.isPending}
            >
              Save changes
            </Button>
          </Row>
        }
      >
        <ContentWrapper spacing="lg" fullWidth>
          <>
            {categorySections.map(({ category, mappings }, index) => (
              <PageSection
                key={category}
                title={ActionCategoryLabels[category]}
                withoutBottomDivider={
                  !showFxOnReconciliation &&
                  index === categorySections.length - 1
                }
              >
                <LazySection index={index}>
                  <Column spacing="lg">
                    <Table<AccountMappingWithOriginalIndex, string>
                      scrollable
                      columns={columns}
                      rows={mappings}
                    />
                    {(category === ActionCategory.Income ||
                      category === ActionCategory.Expense) && (
                      <Row justify="flex-end" fullWidth>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => addCustomMapping(category)}
                          size="large"
                        >
                          Add new
                        </Button>
                      </Row>
                    )}
                  </Column>
                </LazySection>
              </PageSection>
            ))}
            {showFxOnReconciliation ? (
              <PageSection
                key="fx-on-reconciliation"
                title="FX on reconciliation"
                withoutBottomDivider
              >
                <LazySection index={categorySections.length}>
                  <Column spacing="lg">
                    <Typography variant="body2" color="text.secondary">
                      These partial transactions will contribute towards a full
                      transaction on reconciliation.
                    </Typography>
                    <Table<AccountMappingWithOriginalIndex, string>
                      scrollable
                      columns={columns}
                      rows={fxOnReconciliationRows}
                    />
                  </Column>
                </LazySection>
              </PageSection>
            ) : null}
          </>
        </ContentWrapper>
      </FullHeightView>
    </form>
  );
};

export default AccountMappingGrid;
